// src/pages/Accessories/AccessoryItemCard.jsx
import React, { useState } from "react";
import { Box, Typography, Button, Stack, Divider } from "@mui/material";
import QuantitySelector from "../../components/QuantitySelector";
import AccessoryCustomizationForm from "./AccessoryCustomizationForm";
import SnackbarAlert from "../../components/SnackbarAlert";
import { useCart } from "../../context/CartContext";
import AccessoryImageGallery from "./AccessoryImageGallery";
import { isCombinationDisabled } from "../../utils/accessory";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAuth } from "../../context/AuthContext";
import { buildLineId } from "../../utils/lineId";

const normalizeForId = (val) => {
  if (val == null) return null;
  if (typeof val === "string") return val.trim().toLowerCase();
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) return val.map(normalizeForId).join("|");
  if (typeof val === "object") {
    if (val._id) return String(val._id);
    if (val.value != null) return normalizeForId(val.value);
    if (val.label != null) return normalizeForId(val.label);
    return Object.keys(val)
      .sort()
      .map((k) => `${k}:${normalizeForId(val[k])}`)
      .join(",");
  }
  return String(val);
};

const AccessoryItemCard = ({ item }) => {
  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();

  const {
    _id,
    name,
    displayName,
    description,
    images = [],
    price = 0,
    category = "accessory",
    subcategory = null,
    customizationOptions = [],
    disabledCombinations = [],
  } = item;

  const requiresCustomization = customizationOptions.length > 0;
  const [customizationValues, setCustomizationValues] = useState({});
  const [baseQuantity, setBaseQuantity] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const disabled = isCombinationDisabled(
    customizationValues,
    disabledCombinations
  );

  const validateSelections = () => {
    const errors = {};
    customizationOptions.forEach((field) => {
      const value = customizationValues[field.key];
      const isEmpty =
        value === undefined ||
        value === "" ||
        (typeof value === "object" && Object.keys(value).length === 0);

      if (
        (field.type === "select" ||
          field.type === "radio" ||
          field.type === "color" ||
          field.type === "number") &&
        isEmpty
      ) {
        errors[field.key] = "Please select a value.";
      }
    });
    return errors;
  };

  const handleFieldUpdate = (fieldKey, value) => {
    setCustomizationValues((prev) => ({ ...prev, [fieldKey]: value }));
    setValidationErrors((prev) => {
      if (!prev[fieldKey]) return prev;
      const copy = { ...prev };
      delete copy[fieldKey];
      return copy;
    });
  };

  const handleAddToCart = async () => {
    if (disabled) {
      setShowWarning(true);
      return;
    }

    let finalItem;

    if (requiresCustomization) {
      const errors = validateSelections();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setShowWarning(true);
        return;
      }

      // UI customizations (what we render in cart)
      const customizationArray = Object.entries(customizationValues).map(
        ([key, value]) => {
          const cfg = customizationOptions.find((o) => o.key === key);
          return {
            key,
            label: cfg?.label || key,
            value:
              typeof value === "object" && value?.label != null
                ? value.label
                : value,
          };
        }
      );

      // Minimal snapshot for stable id
      const idCustomizations = Object.entries(customizationValues).map(
        ([key, value]) => ({
          key,
          value: normalizeForId(value),
        })
      );

      const type = subcategory || category || "accessory";
      const lineId = buildLineId({
        productId: _id,
        type,
        options: {
          customizations: idCustomizations,
        },
      });

      finalItem = {
        // identity
        lineId,
        productId: _id,
        type,
        category,
        subcategory,

        // display/meta
        name,
        displayName,
        image: images?.[0] || null,

        // options
        size: null,
        flavor: null,
        color: null,

        // details
        customizations: customizationArray,
        customization: customizationArray, // legacy key for compatibility

        // pricing
        listPrice: Number(price),
        unitPrice: Number(price),
        quantity: baseQuantity,
        totalPrice: Number(price) * baseQuantity,
        itemsPerSet: 1,
      };
    } else {
      const type = subcategory || category || "accessory";
      const lineId = buildLineId({
        productId: _id,
        type,
        options: { customizations: [] },
      });

      finalItem = {
        lineId,
        productId: _id,
        type,
        category,
        subcategory,
        name,
        displayName,
        image: images?.[0] || null,
        size: null,
        flavor: null,
        color: null,
        customizations: [],
        customization: [],

        listPrice: Number(price),
        unitPrice: Number(price),
        quantity: baseQuantity,
        totalPrice: Number(price) * baseQuantity,
        itemsPerSet: 1,
      };
    }

    const result = await addToCart(finalItem);

    if (result.ok && result.synced) {
      setSuccessMessage("Added to Cart");
      setShowSuccess(true);
      setCustomizationValues({});
      setBaseQuantity(1);
      setValidationErrors({});
    } else if (result.ok && !result.synced) {
      setSuccessMessage("Added to Cart (sync failed, will retry)");
      setShowSuccess(true);
    } else {
      setShowWarning(true);
    }

    // Analytics
    const valueForAnalytics = Number(price) * baseQuantity;
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_name: displayName || name,
        content_ids: [_id],
        content_type: "product",
        value: valueForAnalytics,
        currency: "CAD",
      });
    }
    if (window.gtag) {
      const variant = customizationOptions
        .map((opt) => customizationValues[opt.key])
        .filter(Boolean)
        .join(", ");
      window.gtag("event", "add_to_cart", {
        currency: "CAD",
        value: valueForAnalytics,
        items: [
          {
            item_id: _id,
            item_name: displayName || name,
            price: Number(price),
            quantity: baseQuantity,
            item_category: category,
            item_variant: variant || undefined,
          },
        ],
      });
    }
  };

  return (
    <Box
      sx={{
        width: 400,
        maxWidth: "100%",
        height: 650,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: 3,
        boxShadow: 2,
        p: 2,
        boxSizing: "border-box",
        marginRight: 20,
        "&:hover": { boxShadow: 5 },
      }}>
      <Box sx={{ width: "fit-content", height: 300, mb: 1 }}>
        <AccessoryImageGallery images={images} />
      </Box>

      <Typography variant="h6" fontWeight="bold" align="center" gutterBottom>
        {displayName}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: 1 }}>
          {description}
        </Typography>
      )}

      <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
        ${Number(price).toFixed(2)}{" "}
        <Typography component="span" variant="body2">
          each
        </Typography>
      </Typography>

      <Box sx={{ flexGrow: 1 }}>
        {requiresCustomization && (
          <AccessoryCustomizationForm
            customizationOptions={customizationOptions}
            customizationValues={customizationValues}
            setCustomizationValues={setCustomizationValues}
            onFieldUpdate={handleFieldUpdate}
            validationErrors={validationErrors}
            disabledCombinations={disabledCombinations}
          />
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          <QuantitySelector
            value={baseQuantity}
            setValue={setBaseQuantity}
            min={1}
          />
        </Box>
        <Button
          variant="contained"
          fullWidth
          sx={{ textTransform: "none", fontSize: "12px", flex: 1 }}
          onClick={handleAddToCart}
          disabled={disabled}>
          Add to Cart
        </Button>
      </Stack>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Stack
        direction="row"
        spacing={1}
        alignItems="left"
        justifyContent="left"
        sx={{ mb: 1 }}>
        <InfoOutlinedIcon fontSize="small" color="action" />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "left" }}>
          Design or color may vary slightly from the photos shown, depending on
          availability.
        </Typography>
      </Stack>

      <SnackbarAlert
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={showSuccess ? successMessage : ""}
        severity="success"
        bgColor="#4caf50"
        iconColor="#fff"
      />
      <SnackbarAlert
        open={showWarning}
        onClose={() => setShowWarning(false)}
        message={
          Object.keys(validationErrors).length > 0
            ? "Please complete all required selections"
            : "Failed to add item to cart. Try again."
        }
        severity="warning"
        bgColor="#ffa000"
        iconColor="#fff"
      />
    </Box>
  );
};

export default AccessoryItemCard;
